"use client";

import { useLocale } from "next-intl";
import { type AnchorHTMLAttributes, forwardRef, type ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/lib/i18n-locales";

type Href =
  | string
  | {
      pathname?: string;
      query?: Record<string, string | number | boolean | undefined>;
      hash?: string;
    };
type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: Href;
  children?: ReactNode;
  locale?: string;
  prefetch?: boolean;
};

export const routing = {
  locales: [...SUPPORTED_LOCALES],
  defaultLocale: DEFAULT_LOCALE,
  localePrefix: "as-needed" as const,
};

function normalizeLocale(locale?: string): SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE;
}

function hrefValue(href: Href): string {
  if (typeof href === "string") return href;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(href.query ?? {})) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return `${href.pathname ?? "/"}${query ? `?${query}` : ""}${href.hash ? `#${href.hash}` : ""}`;
}

function stripLocale(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] && SUPPORTED_LOCALES.includes(segments[0] as SupportedLocale))
    segments.shift();
  const path = `/${segments.join("/")}`;
  return path === "/" ? "/" : path.replace(/\/$/, "");
}

export function getPathname({ href, locale }: { href: Href; locale?: string }) {
  const raw = hrefValue(href);
  if (/^(?:https?:|mailto:|tel:|#)/i.test(raw)) return raw;
  const normalized = normalizeLocale(locale);
  const parsed = new URL(raw, "https://sendbot.co");
  const path = stripLocale(parsed.pathname);
  const localized =
    normalized === DEFAULT_LOCALE
      ? path
      : path === "/"
        ? `/${normalized}`
        : `/${normalized}${path}`;
  return `${localized}${parsed.search}${parsed.hash}`;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function I18nLink(
  { href, locale: localeOverride, prefetch: _prefetch, ...props },
  ref,
) {
  const activeLocale = useLocale();
  return (
    <a
      ref={ref}
      href={getPathname({ href, locale: localeOverride ?? activeLocale })}
      {...props}
    />
  );
});

export function usePathname() {
  return typeof window === "undefined"
    ? "/"
    : stripLocale(window.location.pathname);
}

export function useRouter() {
  const activeLocale = useLocale();
  const navigate = (
    href: Href,
    options?: { locale?: string; scroll?: boolean },
    replace = false,
  ) => {
    const destination = getPathname({
      href,
      locale: options?.locale ?? activeLocale,
    });
    if (replace) window.location.replace(destination);
    else window.location.assign(destination);
  };
  return {
    push: (href: Href, options?: { locale?: string; scroll?: boolean }) =>
      navigate(href, options),
    replace: (href: Href, options?: { locale?: string; scroll?: boolean }) =>
      navigate(href, options, true),
    back: () => window.history.back(),
    forward: () => window.history.forward(),
    refresh: () => window.location.reload(),
    prefetch: async (_href: Href) => undefined,
  };
}

export function redirect({
  href,
  locale,
}: {
  href: Href;
  locale?: string;
}): never {
  const destination = getPathname({ href, locale });
  if (typeof window !== "undefined") window.location.assign(destination);
  throw new Error(`Redirect to ${destination}`);
}
